/**
 * RequirePermission Component
 * Conditionally renders children based on user permissions
 * 
 * Usage:
 * <RequirePermission permission="usuarios.ver">
 *   <UsersTable />
 * </RequirePermission>
 * 
 * Or with multiple permissions (any):
 * <RequirePermission anyOf={['avisos.ver', 'avisos.crear']}>
 *   <AvisosPanel />
 * </RequirePermission>
 * 
 * Or with all permissions required:
 * <RequirePermission allOf={['usuarios.ver', 'usuarios.editar']}>
 *   <EditUserForm />
 * </RequirePermission>
 */
import { useAuth } from '../context/AuthContext';

const RequirePermission = ({
    children,
    permission,    // Single permission code
    anyOf,         // Array of permissions (OR logic)
    allOf,         // Array of permissions (AND logic)
    fallback = null // Component to render if no permission
}) => {
    const { hasPermission, hasAnyPermission, hasAllPermissions, isAdmin } = useAuth();

    // Admins have all permissions
    if (isAdmin()) {
        return children;
    }

    // Single permission check
    if (permission) {
        return hasPermission(permission) ? children : fallback;
    }

    // Any of the permissions (OR)
    if (anyOf && Array.isArray(anyOf)) {
        return hasAnyPermission(anyOf) ? children : fallback;
    }

    // All of the permissions (AND)
    if (allOf && Array.isArray(allOf)) {
        return hasAllPermissions(allOf) ? children : fallback;
    }

    // No permission specified, render children
    return children;
};

export default RequirePermission;
